/* eslint-disable react/prop-types */
import React, { useRef, useCallback, useEffect, useState } from 'react';
import { Box, Button } from '@mui/joy';
import { Print as PrintIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import '@fontsource/noto-sans/latin-400.css';
import '@fontsource/noto-sans/latin-700.css';
import '@fontsource/noto-sans-arabic/arabic-400.css';
import '@fontsource/noto-sans-arabic/arabic-700.css';
import '@fontsource/noto-sans-sc/chinese-simplified-400.css';
import '@fontsource/noto-sans-sc/chinese-simplified-700.css';
import { DOC_FONT_STACK, DOC_PRINT_CSS, DOC_PRINT_FONT_LINK } from './htmlDocumentUtils';

const A4_WIDTH_PX = 794;

const DOC_PREVIEW_CSS = `
${DOC_PRINT_CSS}
.doc-page.doc-page-preview {
  width: ${A4_WIDTH_PX}px;
  max-width: none;
  min-height: 0;
  margin: 0;
  flex-shrink: 0;
}
`;

function useA4PreviewScale(containerRef, pageRef) {
  const [scale, setScale] = useState(1);
  const [pageHeight, setPageHeight] = useState(0);

  useEffect(() => {
    const container = containerRef.current;
    const page = pageRef.current;
    if (!container || !page) return undefined;

    const update = () => {
      const availableWidth = container.clientWidth;
      const nextScale = availableWidth >= A4_WIDTH_PX ? 1 : availableWidth / A4_WIDTH_PX;
      setScale(nextScale);
      setPageHeight(page.offsetHeight);
    };

    update();
    const observer = new ResizeObserver(update);
    observer.observe(container);
    observer.observe(page);
    return () => observer.disconnect();
  }, [containerRef, pageRef]);

  return { scale, pageHeight };
}

function buildPrintHtml({ title, isRtl, contentHtml }) {
  const dir = isRtl ? 'rtl' : 'ltr';
  const safeTitle = String(title || 'Document').replace(/</g, '&lt;');

  return `<!DOCTYPE html>
<html dir="${dir}">
<head>
<meta charset="utf-8" />
<base href="${window.location.origin}/" />
<title>${safeTitle}</title>
<link rel="stylesheet" href="${DOC_PRINT_FONT_LINK}" />
<style>${DOC_PRINT_CSS}</style>
</head>
<body>
<div class="doc-page">${contentHtml}</div>
</body>
</html>`;
}

function clonePrintContent(node) {
  const contentClone = node.cloneNode(true);
  contentClone.querySelectorAll('img[src]').forEach((img) => {
    const src = img.getAttribute('src');
    if (!src || (img.complete && img.naturalWidth === 0)) {
      img.remove();
      return;
    }
    try {
      img.setAttribute('src', new URL(src, window.location.href).href);
    } catch {
      img.remove();
    }
  });
  return contentClone.innerHTML;
}

function waitForDocumentImages(doc) {
  const images = Array.from(doc?.images || []);
  if (images.length === 0) return Promise.resolve();

  return Promise.all(
    images.map(
      (img) =>
        new Promise((resolve) => {
          if (img.complete) {
            resolve();
            return;
          }
          img.onload = () => resolve();
          img.onerror = () => resolve();
        }),
    ),
  );
}

export default function HtmlDocumentViewer({ title, isRtl, compact = false, children }) {
  const { t } = useTranslation('ui');
  const previewContainerRef = useRef(null);
  const contentRef = useRef(null);
  const { scale, pageHeight } = useA4PreviewScale(previewContainerRef, contentRef);

  const handlePrint = useCallback(() => {
    const node = contentRef.current;
    if (!node) return;

    const printHtml = buildPrintHtml({
      title,
      isRtl,
      contentHtml: clonePrintContent(node),
    });

    const iframe = document.createElement('iframe');
    iframe.setAttribute('aria-hidden', 'true');
    iframe.title = String(title || 'Document');
    iframe.style.cssText =
      'position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden;';
    document.body.appendChild(iframe);

    const cleanup = () => {
      if (iframe.parentNode) {
        iframe.parentNode.removeChild(iframe);
      }
    };

    const iframeWindow = iframe.contentWindow;
    const iframeDoc = iframe.contentDocument || iframeWindow?.document;
    if (!iframeDoc || !iframeWindow) {
      cleanup();
      return;
    }

    iframeDoc.open();
    iframeDoc.write(printHtml);
    iframeDoc.close();

    let printed = false;
    const triggerPrint = () => {
      if (printed) return;
      printed = true;
      iframeWindow.focus();
      iframeWindow.print();
      if ('onafterprint' in iframeWindow) {
        iframeWindow.onafterprint = cleanup;
      } else {
        setTimeout(cleanup, 1000);
      }
    };

    const prepareAndPrint = () => {
      waitForDocumentImages(iframeDoc).then(() => {
        setTimeout(triggerPrint, 200);
      });
    };

    iframe.onload = prepareAndPrint;
    setTimeout(prepareAndPrint, 800);
  }, [title, isRtl]);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1.5 }}>
        <Button
          size="sm"
          variant="outlined"
          startDecorator={<PrintIcon fontSize="small" />}
          onClick={handlePrint}
        >
          {t('pdfDocuments.common.printSavePdf')}
        </Button>
      </Box>
      <Box
        ref={previewContainerRef}
        sx={{
          display: 'flex',
          justifyContent: 'center',
          bgcolor: '#e5e7eb',
          p: 2,
          borderRadius: 1,
          maxHeight: 600,
          overflow: 'auto',
        }}
      >
        <style>{DOC_PREVIEW_CSS}</style>
        <Box
          sx={{
            width: scale < 1 ? A4_WIDTH_PX * scale : A4_WIDTH_PX,
            height: scale < 1 && pageHeight ? pageHeight * scale : 'auto',
            position: 'relative',
            flexShrink: 0,
          }}
        >
          <Box
            ref={contentRef}
            className="doc-page doc-page-preview"
            dir={isRtl ? 'rtl' : 'ltr'}
            sx={{
              width: A4_WIDTH_PX,
              bgcolor: '#fff',
              color: '#0f172a',
              fontSize: compact ? '9pt' : '10pt',
              lineHeight: compact ? 1.35 : 1.45,
              px: compact ? '44px' : '56px',
              py: compact ? '30px' : '36px',
              boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
              fontFamily: DOC_FONT_STACK,
              transform: scale < 1 ? `scale(${scale})` : 'none',
              transformOrigin: 'top left',
              position: scale < 1 ? 'absolute' : 'relative',
              top: 0,
              left: 0,
            }}
          >
            {children}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
